import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react'
import clsx from 'clsx'
import { X } from 'lucide-react'
import { type PointerEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCanvasNodeContext } from '@/features/studio/canvas/CanvasNodeContext'
import { useCanvasTheme } from '@/features/studio/canvas/theme/CanvasThemeContext'
import { EDGE_ANIMATIONS, EDGE_MULTI_LAYER, type EdgeAnimId } from '@/features/studio/canvas/theme/registry'
import { Button } from '@/shared/components/ui/button'

const STROKE = '#64748b'
const STROKE_SELECTED = '#e2e8f0'
const TRACK_STROKE = 'rgba(15, 23, 42, 0.92)'

const STATIC_ANIMS = new Set<EdgeAnimId>(['solid', 'heartbeat'])

function edgeAccent(anim: EdgeAnimId): string {
  return EDGE_ANIMATIONS.find((a) => a.id === anim)?.accent ?? STROKE
}

function edgeStrokeWidth(anim: EdgeAnimId, selected: boolean): number {
  if (selected) return 2.75
  if (anim === 'flow-stream' || anim === 'flow-comet' || anim === 'flow-neon') return 3
  if (anim === 'pipe' || anim === 'lane') return 3
  return 2.5
}

export function StudioEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  interactionWidth = 24,
}: EdgeProps) {
  const { t } = useTranslation()
  const { onDeleteEdge } = useCanvasNodeContext()
  const { edgeAnim } = useCanvasTheme()
  const handleDelete = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()
      onDeleteEdge?.([id])
    },
    [id, onDeleteEdge],
  )
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const animated = data?.animated !== false
  const layers = EDGE_MULTI_LAYER[edgeAnim]
  const gradId = `cn-edge-grad-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`
  const useGradient = edgeAnim === 'gradient-shift'
  const showTrack = animated && !selected && !STATIC_ANIMS.has(edgeAnim)
  const accent = edgeAccent(edgeAnim)

  const mainClass = clsx(
    'cn-edge-main',
    animated && !selected && `cn-edge-main--${edgeAnim}`,
    selected && 'cn-edge-main--selected',
  )

  const stroke = selected ? STROKE_SELECTED : useGradient ? `url(#${gradId})` : accent
  const strokeWidth = edgeStrokeWidth(edgeAnim, !!selected)

  return (
    <>
      {useGradient ? (
        <defs>
          <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1={sourceX} y1={sourceY} x2={targetX} y2={targetY}>
            <stop offset="0%" stopColor="#6366f1" className="cn-edge-grad-stop-a" />
            <stop offset="50%" stopColor="#22d3ee" className="cn-edge-grad-stop-b" />
            <stop offset="100%" stopColor="#a855f7" className="cn-edge-grad-stop-c" />
          </linearGradient>
        </defs>
      ) : null}

      {showTrack ? (
        <path
          d={edgePath}
          fill="none"
          stroke={TRACK_STROKE}
          strokeWidth={strokeWidth + 2.5}
          strokeLinecap="round"
          className="cn-edge-track"
        />
      ) : null}

      {/* 副层：双轨 / 编织 / 轨道 / 车道 / 霓虹 */}
      {layers >= 2 && animated && !selected ? (
        <>
          {(edgeAnim === 'double-track' || edgeAnim === 'braid' || edgeAnim === 'flow-neon') && (
            <path
              d={edgePath}
              fill="none"
              stroke={edgeAnim === 'flow-neon' ? '#22d3ee' : accent}
              strokeWidth={edgeAnim === 'flow-neon' ? 2.5 : 1.5}
              strokeLinecap="round"
              opacity={edgeAnim === 'flow-neon' ? 0.85 : 0.45}
              className={clsx(
                'cn-edge-sub',
                edgeAnim === 'flow-neon' ? 'cn-edge-sub--flow-neon' : `cn-edge-sub--${edgeAnim}`,
              )}
            />
          )}
          {(edgeAnim === 'rail' || edgeAnim === 'lane') && (
            <path
              d={edgePath}
              fill="none"
              stroke={accent}
              strokeWidth={1.5}
              strokeLinecap="round"
              opacity={0.4}
              className={clsx('cn-edge-sub', edgeAnim === 'lane' ? 'cn-edge-sub--lane-side' : 'cn-edge-sub--rail-side')}
            />
          )}
        </>
      ) : null}

      <BaseEdge
        id={id}
        path={edgePath}
        interactionWidth={interactionWidth}
        style={{ stroke, strokeWidth, strokeLinecap: 'round' }}
        className={mainClass}
      />

      {onDeleteEdge ? (
        <EdgeLabelRenderer>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="nodrag nopan cn-edge-delete absolute -translate-x-1/2 -translate-y-1/2"
            style={{
              left: labelX,
              top: labelY,
              zIndex: 10002,
              pointerEvents: 'all',
              opacity: selected ? 1 : undefined,
            }}
            onPointerDown={handleDelete}
            aria-label={t('canvas.edgeDelete')}
            title={t('canvas.edgeDelete')}
          >
            <X className="h-3 w-3" />
          </Button>
        </EdgeLabelRenderer>
      ) : null}
    </>
  )
}
